import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { PayabliPaymentMethodSetupForm } from '@/components/organisms/payments/PayabliPaymentMethodSetupForm';
import type { PayabliRail } from '@/lib/payments/payabli';
import type { PayabliPublicPageConfig } from '@/types/public';

/**
 * Payabli checkout for the public payment link and public tip pages.
 *
 * Payabli has no hosted-checkout redirect: the embedded component captures the
 * card inline and hands back a single-use temporary token, which we POST to the
 * page's own charge endpoint. The server charges the amount IT computed — the
 * browser never sends a figure — so the amount rendered here is a disclosure,
 * not an instruction.
 *
 * The invoice page has its own copy of this shape (it reads the payer's contact
 * off the invoice rather than off a page config); the two are deliberately not
 * merged so a change to the invoice flow can't silently alter these pages.
 */
export function PayabliPublicCheckout({
  config,
  submitLabel,
  onCharge,
  onCancel,
  onRailChange,
  disabled,
}: {
  config: PayabliPublicPageConfig;
  /** Exact total for a fixed rail, or a neutral label when the rail determines the total. */
  submitLabel: string;
  /** Runs the server-side charge. Throw to let the form show its own error. */
  onCharge: (temporaryToken: string) => Promise<void>;
  onCancel?: () => void;
  /**
   * The payer switched rails inside the embedded form. Pages that disclose a
   * card service fee MUST follow this: the fee, the amount on the button and
   * the `surchargeDisclosed` attestation only ever apply to the card rail.
   */
  onRailChange?: (rail: PayabliRail) => void;
  disabled?: boolean;
}) {
  // The shared setup form re-enables its submit button as soon as tokenization
  // succeeds — before the server-side charge below finishes. Track our own
  // in-flight state and swap the form out while the charge runs, so a second
  // click can't tokenize again and fire a second charge (a fresh token means a
  // different idempotency key, which the server cannot dedupe against).
  const [charging, setCharging] = useState(false);

  if (!config.publicToken || !config.entryPoint || !config.customer) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Online payment is temporarily unavailable. Please contact the business to pay this.
      </p>
    );
  }

  if (charging) {
    return (
      <div className="flex flex-col items-center gap-2 py-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Processing your payment…</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PayabliPaymentMethodSetupForm
        appearance="customer"
        holderName={
          [config.customer.firstName, config.customer.lastName].filter(Boolean).join(' ') ||
          undefined
        }
        publicToken={config.publicToken}
        entryPoint={config.entryPoint}
        mode={config.mode}
        submitLabel={submitLabel}
        // Only render the rails the tenant actually accepts, so a payer never
        // enters details the server-side charge would reject. The backend
        // always sends both flags (mirroring the charge-time gate) — there is
        // no `?? true` fallback that could re-expose a disabled rail.
        methods={{ card: config.acceptCard, ach: config.acceptACH }}
        onRailChange={onRailChange}
        errorTitle="Payment failed"
        // Required: paypoints that demand a customer identifier reject
        // tokenization without one, and the server binds the stored method to
        // this same contact id so Payabli's payor match passes.
        customerData={config.customer}
        onComplete={async (result) => {
          setCharging(true);
          try {
            await onCharge(result.temporaryToken);
          } catch (error) {
            // Re-enable the form so the payer can retry with a fresh entry —
            // the spent token can never be replayed.
            setCharging(false);
            throw error;
          }
        }}
      />
      {onCancel && !disabled && (
        <button
          type="button"
          onClick={onCancel}
          className="w-full rounded-lg py-2 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
